import { MAX_MAP_DIM } from "../effects/limits.ts";
import { CROSSING_KINDS, SEAM_STATES } from "../document/layout.ts";
import type { SeamHead } from "../document/layout-commands.ts";
import type { MapAtmosphere, MapGroup, MapHost, MapLight, MapPlace, MapShare, SeamCrossing, WorldGrid } from "../document/types.ts";
import { distinctOf, emptyLayerOf, groupOf, hasOnly, isId, isIntegerIn, isName, isText, jsonRecordOf, listOf, recordOf, type Fields } from "./op-read.ts";
import { OP_MAP_SIDE_MAX, type EmptyMap, type Intent } from "./op-types.ts";

/**
 * How the layout's values and intents read from an operation (ADR-109 decision 10, ADR-122 decision 1): a place
 * with its depth, the grid, a map group, a crossing, a room's host, sharing and atmosphere, a map added with its
 * layout, and each layout intent. Each reader takes exactly its fields, each of its type, and answers undefined for anything else; what
 * a value means in a document (the lattice, a range, a link) is the resolver's, which knows the document.
 */

const COLOUR = /^#[0-9a-fA-F]{6}$/;

/**
 * Every way a map may be shared.
 */
export const SHARE_WORDS: readonly MapShare[] = ["open", "party", "solo", "owner"];

/**
 * Every light a map may have.
 */
const LIGHT_WORDS: readonly MapLight[] = ["sky", "indoor"];

/**
 * Whether a value is a whole number an operation may carry as a world coordinate or a depth.
 *
 * @param value The value.
 * @returns True for a safe integer.
 */
function isCoordinate(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value);
}

/**
 * Reads a place.
 *
 * @param value The value.
 * @returns The place, or undefined when it is not exactly `{ x, y, depth? }` of whole numbers.
 */
export function placeOf(value: unknown): MapPlace | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["x", "y"], ["depth"]) || !isCoordinate(fields["x"]) || !isCoordinate(fields["y"]) || (fields["depth"] !== undefined && !isCoordinate(fields["depth"]))) {
    return undefined;
  }
  return { x: fields["x"], y: fields["y"], ...(fields["depth"] === undefined ? {} : { depth: fields["depth"] as number }) };
}

/**
 * Reads a grid.
 *
 * @param value The value.
 * @returns The grid, or undefined when it is not exactly `{ cell, x, y }`, the cell 1 to `OP_MAP_SIDE_MAX`.
 */
export function gridOf(value: unknown): WorldGrid | undefined {
  const fields = recordOf(value);
  return fields !== undefined && hasOnly(fields, ["cell", "x", "y"]) && isIntegerIn(fields["cell"], 1, OP_MAP_SIDE_MAX) && isCoordinate(fields["x"]) && isCoordinate(fields["y"]) ? { cell: fields["cell"], x: fields["x"], y: fields["y"] } : undefined;
}

/**
 * Whether a value is a name that is not blank.
 *
 * @param value The value.
 * @returns True for a name holding more than white space.
 */
function isFilledName(value: unknown): value is string {
  return isName(value) && value.trim() !== "";
}

/**
 * Reads a map group.
 *
 * @param value The value.
 * @returns The group, or undefined when it is not exactly `{ id, name, colour }` with an id, a name that is not
 *   blank and a colour of the form `#rrggbb`.
 */
export function mapGroupOf(value: unknown): MapGroup | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["id", "name", "colour"])) {
    return undefined;
  }
  const { id, name, colour } = fields;
  return isId(id) && isFilledName(name) && typeof colour === "string" && COLOUR.test(colour) ? { id, name, colour } : undefined;
}

/**
 * Reads a crossing.
 *
 * @param value The value.
 * @returns The crossing, or undefined when it is not exactly one: a name that is not blank, a kind, a span
 *   given by both ends or neither, each 0 to `OP_MAP_SIDE_MAX`, and `fixed` only as true.
 */
export function crossingOf(value: unknown): SeamCrossing | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["name", "kind"], ["from", "to", "fixed"])) {
    return undefined;
  }
  const { name, kind, from, to, fixed } = fields;
  if (!isText(name, 1) || name.trim() === "" || !(CROSSING_KINDS as readonly unknown[]).includes(kind) || (from === undefined) !== (to === undefined) || (fixed !== undefined && fixed !== true)) {
    return undefined;
  }
  if (from !== undefined && !(isIntegerIn(from, 0, OP_MAP_SIDE_MAX) && isIntegerIn(to, 0, OP_MAP_SIDE_MAX))) {
    return undefined;
  }
  return { name, kind: kind as SeamCrossing["kind"], ...(from === undefined ? {} : { from: from as number, to: to as number }), ...(fixed === true ? { fixed: true as const } : {}) };
}

/**
 * Reads a room's host.
 *
 * @param value The value.
 * @returns The host, or undefined when it is not exactly `{ map, arrival }` of ids.
 */
export function hostOf(value: unknown): MapHost | undefined {
  const fields = recordOf(value);
  return fields !== undefined && hasOnly(fields, ["map", "arrival"]) && isId(fields["map"]) && isId(fields["arrival"]) ? { map: fields["map"], arrival: fields["arrival"] } : undefined;
}

/**
 * Whether a value is a word of sharing.
 *
 * @param value The value.
 * @returns True for one of `SHARE_WORDS`.
 */
export function isShare(value: unknown): value is MapShare {
  return (SHARE_WORDS as readonly unknown[]).includes(value);
}

/**
 * Whether a value is a word of light.
 *
 * @param value The value.
 * @returns True for `sky` or `indoor`.
 */
export function isLight(value: unknown): value is MapLight {
  return (LIGHT_WORDS as readonly unknown[]).includes(value);
}

/**
 * Whether a value is a dim: a finite number from 0 to `MAX_MAP_DIM`, the range the compile holds a map to.
 *
 * @param value The value.
 * @returns True when it is.
 */
function isDim(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= MAX_MAP_DIM;
}

/**
 * Reads an atmosphere carried whole, as an added map carries it.
 *
 * @param value The value.
 * @returns The atmosphere, or undefined when it is not exactly one with at least one field: names of 1 to
 *   `OP_TEXT_MAX` code units and a light word.
 */
function atmosphereOf(value: unknown): MapAtmosphere | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, [], ["music", "ambience", "light", "dim"]) || Object.keys(fields).length === 0) {
    return undefined;
  }
  const { music, ambience, light, dim } = fields;
  if ((music !== undefined && !isText(music, 1)) || (ambience !== undefined && !isText(ambience, 1)) || (light !== undefined && !isLight(light)) || (dim !== undefined && !isDim(dim))) {
    return undefined;
  }
  return { ...(music === undefined ? {} : { music: music as string }), ...(ambience === undefined ? {} : { ambience: ambience as string }), ...(light === undefined ? {} : { light: light as MapLight }), ...(dim === undefined ? {} : { dim: dim as number }) };
}

/**
 * Reads the layout an added map carries (ADR-109 decision 10, ADR-122 decision 1): its place, host, sharing
 * (never `open`, which is none), capacity, atmosphere, group and draft mark, each optional, the mark only as
 * true.
 *
 * @param fields The map's fields.
 * @returns The fields present, or undefined when one is not of its type.
 */
function mapLayoutOf(fields: Fields): Pick<EmptyMap, "place" | "host" | "share" | "capacity" | "atmosphere" | "mapGroup" | "draft"> | undefined {
  const { mapGroup, draft, share, capacity } = fields;
  const place = fields["place"] === undefined ? null : placeOf(fields["place"]);
  const host = fields["host"] === undefined ? null : hostOf(fields["host"]);
  const atmosphere = fields["atmosphere"] === undefined ? null : atmosphereOf(fields["atmosphere"]);
  if (place === undefined || host === undefined || atmosphere === undefined || (mapGroup !== undefined && !isId(mapGroup)) || (draft !== undefined && draft !== true)) {
    return undefined;
  }
  if ((share !== undefined && !(isShare(share) && share !== "open")) || (capacity !== undefined && !isIntegerIn(capacity, 0, OP_MAP_SIDE_MAX))) {
    return undefined;
  }
  return {
    ...(place === null ? {} : { place }),
    ...(host === null ? {} : { host }),
    ...(share === undefined ? {} : { share: share as Exclude<MapShare, "open"> }),
    ...(capacity === undefined ? {} : { capacity: capacity as number }),
    ...(atmosphere === null ? {} : { atmosphere }),
    ...(mapGroup === undefined ? {} : { mapGroup }),
    ...(draft === true ? { draft: true as const } : {}),
  };
}

/**
 * Reads a map without layer content.
 *
 * @param value The value.
 * @returns The map, or undefined when it is not exactly one, two layers or two groups share an id, a layer
 *   names a group the map lacks, or `first` names no layer of it.
 */
export function emptyMapOf(value: unknown): EmptyMap | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["id", "name", "width", "height", "seed", "first", "groups", "layers", "properties"], ["place", "host", "share", "capacity", "atmosphere", "mapGroup", "draft"])) {
    return undefined;
  }
  const layout = mapLayoutOf(fields);
  if (layout === undefined) {
    return undefined;
  }
  const { id, name, width, height, seed, first } = fields;
  const groups = listOf(fields["groups"], groupOf);
  const layers = listOf(fields["layers"], emptyLayerOf);
  const properties = jsonRecordOf(fields["properties"]);
  if (!isId(id) || !isName(name) || !isIntegerIn(width, 1, OP_MAP_SIDE_MAX) || !isIntegerIn(height, 1, OP_MAP_SIDE_MAX) || !isIntegerIn(seed, 0, 0xffffffff) || groups === undefined || layers === undefined || properties === undefined) {
    return undefined;
  }
  const groupIds = new Set(groups.map((group) => group.id));
  const layerIds = new Set(layers.map((layer) => layer.id));
  if (groupIds.size !== groups.length || layerIds.size !== layers.length || layers.some((layer) => layer.group !== undefined && !groupIds.has(layer.group))) {
    return undefined;
  }
  if (first !== null && !(isId(first) && layerIds.has(first))) {
    return undefined;
  }
  return { id, name, width, height, seed, ...layout, first, groups, layers, properties };
}

/**
 * Reads a change of a map's atmosphere: each of its music, ambience and light named is set, or cleared by null.
 *
 * @param fields The intent's fields.
 * @returns The intent, or undefined when it names none of the three or one is not of its type.
 */
function atmosphereIntentOf(fields: Fields): Intent | undefined {
  const { map, music, ambience, light, dim } = fields;
  const nameOrNone = (value: unknown): boolean => value === undefined || value === null || isText(value, 1);
  if (!hasOnly(fields, ["k", "map"], ["music", "ambience", "light", "dim"]) || !isId(map) || (music === undefined && ambience === undefined && light === undefined && dim === undefined) || !(dim === undefined || dim === null || isDim(dim)) || !nameOrNone(music) || !nameOrNone(ambience) || !(light === undefined || light === null || isLight(light))) {
    return undefined;
  }
  return { k: "setMapAtmosphere", map, ...(music === undefined ? {} : { music: music as string | null }), ...(ambience === undefined ? {} : { ambience: ambience as string | null }), ...(light === undefined ? {} : { light: light as MapLight | null }), ...(dim === undefined ? {} : { dim: dim as number | null }) };
}

/**
 * Whether two values name a seam's pair: two different map ids.
 *
 * @param a The first.
 * @param b The second.
 * @returns True for two distinct ids.
 */
function isPair(a: unknown, b: unknown): boolean {
  return isId(a) && isId(b) && a !== b;
}

/**
 * Reads a layout intent.
 *
 * @param fields The intent's fields.
 * @returns The intent rebuilt with exactly its fields, or undefined when it is not exactly one.
 */
export function layoutIntentOf(fields: Fields): Intent | undefined {
  const { map, a, b } = fields;
  switch (fields["k"]) {
    case "placeMap": {
      const place = fields["place"] === null ? null : placeOf(fields["place"]);
      return hasOnly(fields, ["k", "map", "place"]) && isId(map) && place !== undefined ? { k: "placeMap", map, place } : undefined;
    }
    case "setGrid": {
      const grid = fields["grid"] === null ? null : gridOf(fields["grid"]);
      return hasOnly(fields, ["k", "grid"]) && grid !== undefined ? { k: "setGrid", grid } : undefined;
    }
    case "setMapGroups": {
      const set = fields["set"] === undefined ? null : listOf(fields["set"], mapGroupOf);
      const remove = fields["remove"] === undefined ? null : distinctOf(fields["remove"], isId);
      if (!hasOnly(fields, ["k"], ["set", "remove"]) || set === undefined || remove === undefined || (set === null && remove === null) || (set !== null && set.length === 0) || (remove !== null && remove.length === 0)) {
        return undefined;
      }
      const ids = new Set((set ?? []).map((group) => group.id));
      if (ids.size !== (set ?? []).length || (remove ?? []).some((id) => ids.has(id))) {
        return undefined;
      }
      return { k: "setMapGroups", ...(set === null ? {} : { set }), ...(remove === null ? {} : { remove }) };
    }
    case "setMapGroup": {
      const group = fields["group"];
      return hasOnly(fields, ["k", "map", "group"]) && isId(map) && (group === null || isId(group)) ? { k: "setMapGroup", map, group } : undefined;
    }
    case "setMapDraft": {
      const draft = fields["draft"];
      return hasOnly(fields, ["k", "map", "draft"]) && isId(map) && typeof draft === "boolean" ? { k: "setMapDraft", map, draft } : undefined;
    }
    case "setMapHost": {
      const host = fields["host"] === null ? null : hostOf(fields["host"]);
      return hasOnly(fields, ["k", "map", "host"]) && isId(map) && host !== undefined ? { k: "setMapHost", map, host } : undefined;
    }
    case "setMapShare": {
      const share = fields["share"];
      return hasOnly(fields, ["k", "map", "share"]) && isId(map) && isShare(share) ? { k: "setMapShare", map, share } : undefined;
    }
    case "setMapCapacity": {
      const capacity = fields["capacity"];
      return hasOnly(fields, ["k", "map", "capacity"]) && isId(map) && (capacity === null || isIntegerIn(capacity, 0, OP_MAP_SIDE_MAX)) ? { k: "setMapCapacity", map, capacity } : undefined;
    }
    case "setMapAtmosphere":
      return atmosphereIntentOf(fields);
    case "setSeam": {
      const { state, barrier } = fields;
      if (!hasOnly(fields, ["k", "a", "b", "state"], ["barrier"]) || !isPair(a, b) || !(SEAM_STATES as readonly unknown[]).includes(state) || (barrier !== undefined && !isText(barrier, 1))) {
        return undefined;
      }
      return { k: "setSeam", a: a as string, b: b as string, state: state as SeamHead["state"], ...(barrier === undefined ? {} : { barrier: barrier as string }) };
    }
    case "removeSeam":
      return hasOnly(fields, ["k", "a", "b"]) && isPair(a, b) ? { k: "removeSeam", a: a as string, b: b as string } : undefined;
    case "setCrossing": {
      const crossing = crossingOf(fields["crossing"]);
      return hasOnly(fields, ["k", "a", "b", "crossing"]) && isPair(a, b) && crossing !== undefined ? { k: "setCrossing", a: a as string, b: b as string, crossing } : undefined;
    }
    case "removeCrossing": {
      const name = fields["name"];
      return hasOnly(fields, ["k", "a", "b", "name"]) && isPair(a, b) && isText(name, 1) ? { k: "removeCrossing", a: a as string, b: b as string, name } : undefined;
    }
    default:
      return undefined;
  }
}
